import { useCallback, useEffect, useState, useRef } from "react";
import { useShallow } from "zustand/shallow";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { $findMatchingParent, $getNearestNodeOfType, mergeRegister } from "@lexical/utils";
import { $isLinkNode, TOGGLE_LINK_COMMAND } from "@lexical/link";
import clsx from "clsx";
import {
  $addUpdateTag,
  $createParagraphNode,
  $getSelection,
  $isRangeSelection,
  $isRootOrShadowRoot,
  CAN_REDO_COMMAND,
  CAN_UNDO_COMMAND,
  FORMAT_TEXT_COMMAND,
  INDENT_CONTENT_COMMAND,
  LexicalEditor,
  OUTDENT_CONTENT_COMMAND,
  REDO_COMMAND,
  SELECTION_CHANGE_COMMAND,
  SKIP_SELECTION_FOCUS_TAG,
  UNDO_COMMAND,
} from "lexical";
import { Tooltip, useDisclosure } from "@heroui/react";
import { DrawerAndModal } from "@/shared/components/common/DrawerAndModal";
import { TOOLTIP_DELAY } from "@/config/constants";
import { $getSelectionStyleValueForProperty, $patchStyleText, $setBlocksType } from "@lexical/selection";
import {
  $isListNode,
  INSERT_CHECK_LIST_COMMAND,
  INSERT_ORDERED_LIST_COMMAND,
  INSERT_UNORDERED_LIST_COMMAND,
  ListNode,
} from "@lexical/list";
import { $createHeadingNode, $createQuoteNode, $isHeadingNode, HeadingTagType } from "@lexical/rich-text";

import { useBoundStore } from "@/app/store";
import { ColorPicker, ColorType } from "../components/ColorPicker";
import { EditorButton } from "../components/EditorButton";
import { TEXT_BG_COLORS, TEXT_COLORS, TOOLBAR_ITEMS_MAP } from "../../../config";
import { ToolbarItemKey } from "../../../types";
import { useTheme } from "next-themes";
import { sanitizeUrl } from "@/shared/utils/url";
import { getSelectedNode } from "@/shared/utils/lexical";
import { INSERT_TABLE_COMMAND } from "@lexical/table";
import { INSERT_HORIZONTAL_RULE_COMMAND } from "@lexical/react/LexicalHorizontalRuleNode";
import { $createCodeNode } from "@lexical/code";
import { InsertEquationDialog } from "../plugins/EquationsPlugin";

const LowPriority = 1;

interface ToolbarPluginProps {
  className?: string;
  onSetIsLinkEditMode: (isLinkEditMode: boolean) => void;
}

export function ToolbarPlugin({ className, onSetIsLinkEditMode }: ToolbarPluginProps) {
  const toolbarItems = useBoundStore(useShallow((state) => state.settings.notes.editorToolbarItems));
  const [editor] = useLexicalComposerContext();
  const { theme } = useTheme();
  const {
    isOpen: isEquationModalOpen,
    onOpen: onEquationModalOpen,
    onOpenChange: onEquationModalOpenChange,
  } = useDisclosure();
  const [canUndo, setCanUndo] = useState(false);
  const [canRedo, setCanRedo] = useState(false);
  const [isBold, setIsBold] = useState(false);
  const [isItalic, setIsItalic] = useState(false);
  const [isUnderline, setIsUnderline] = useState(false);
  const [isStrikethrough, setIsStrikethrough] = useState(false);
  const [isLink, setIsLink] = useState(false);
  const [isCode, setIsCode] = useState(false);
  const [blockType, setBlockType] = useState<BlockType>("paragraph");
  const [showColorPicker, setShowColorPicker] = useState<ColorType | null>(null);
  const [textColor, setTextColor] = useState<string>();
  const [textBgColor, setTextBgColor] = useState<string>();
  const [colorPickerPos, setColorPickerPos] = useState<{ top: number; left: number } | null>(null);
  const textColorButtonRef = useRef<HTMLDivElement>(null);
  const bgColorButtonRef = useRef<HTMLDivElement>(null);
  const deviceType = useBoundStore(useShallow((state) => state.device.type));
  const isMobile = deviceType === "mobile";

  // Filter enabled toolbar items
  const enabledToolbarItemKeys = toolbarItems.filter((item) => item.enabled).map((item) => item.id);

  const applyStyleText = useCallback(
    (styles: Record<string, string | null>, skipHistoryStack?: boolean) => {
      editor.update(
        () => {
          const selection = $getSelection();
          if (selection !== null) {
            $patchStyleText(selection, styles);
          }
        },
        skipHistoryStack ? { tag: "historic" } : {}
      );
    },
    [editor]
  );

  const changeTextColor = useCallback(
    (value: string | null, skipHistoryStack: boolean) => {
      applyStyleText({ color: value }, skipHistoryStack);
    },
    [applyStyleText]
  );

  const changeBackgroundColor = useCallback(
    (value: string | null, skipHistoryStack: boolean) => {
      applyStyleText({ "background-color": value }, skipHistoryStack);
    },
    [applyStyleText]
  );

  const insertLink = useCallback(() => {
    if (!isLink) {
      onSetIsLinkEditMode(true);
      editor.dispatchCommand(TOGGLE_LINK_COMMAND, sanitizeUrl("https://"));
    } else {
      onSetIsLinkEditMode(false);
      editor.dispatchCommand(TOGGLE_LINK_COMMAND, null);
    }
  }, [editor, onSetIsLinkEditMode, isLink]);

  function handleColorSelect(type: ColorType, color: string | null) {
    if (type === "text") {
      changeTextColor(color, false);
    } else {
      changeBackgroundColor(color, false);
    }

    setShowColorPicker(null);
  }

  const $updateToolbar = useCallback(() => {
    const selection = $getSelection();
    if ($isRangeSelection(selection)) {
      // Update text format
      setIsBold(selection.hasFormat("bold"));
      setIsItalic(selection.hasFormat("italic"));
      setIsUnderline(selection.hasFormat("underline"));
      setIsStrikethrough(selection.hasFormat("strikethrough"));
      setIsCode(selection.hasFormat("code"));

      const anchorNode = selection.anchor.getNode();
      let element =
        anchorNode.getKey() === "root"
          ? anchorNode
          : $findMatchingParent(anchorNode, (e) => {
              const parent = e.getParent();
              return parent !== null && $isRootOrShadowRoot(parent);
            });

      if (element === null) {
        element = anchorNode.getTopLevelElementOrThrow();
      }

      const elementKey = element.getKey();
      const elementDOM = editor.getElementByKey(elementKey);

      // Update links
      const node = getSelectedNode(selection);
      const parent = node.getParent();
      const isLink = $isLinkNode(parent) || $isLinkNode(node);
      setIsLink(isLink);

      if (elementDOM !== null) {
        if ($isListNode(element)) {
          const parentList = $getNearestNodeOfType<ListNode>(anchorNode, ListNode);
          const type = parentList ? parentList.getListType() : element.getListType();

          setBlockType(type);
        } else {
          const type = $isHeadingNode(element) ? element.getTag() : element.getType();
          if (blockTypes.includes(type as BlockType)) {
            setBlockType(type as BlockType);
          }
        }

        setTextColor($getSelectionStyleValueForProperty(selection, "color", theme === "light" ? "#000" : "#fff"));
        setTextBgColor(
          $getSelectionStyleValueForProperty(selection, "background-color", theme === "light" ? "#fff" : "#000")
        );
      }
    }
  }, [editor, theme]);

  function handleUndo() {
    editor.dispatchCommand(UNDO_COMMAND, undefined);

    setShowColorPicker(null);
  }

  function handleRedo() {
    editor.dispatchCommand(REDO_COMMAND, undefined);

    setShowColorPicker(null);
  }

  function handleBold() {
    editor.dispatchCommand(FORMAT_TEXT_COMMAND, "bold");
    setShowColorPicker(null);
  }

  function handleItalic() {
    editor.dispatchCommand(FORMAT_TEXT_COMMAND, "italic");
    setShowColorPicker(null);
  }

  function handleUnderline() {
    editor.dispatchCommand(FORMAT_TEXT_COMMAND, "underline");
    setShowColorPicker(null);
  }

  function handleStrikethrough() {
    editor.dispatchCommand(FORMAT_TEXT_COMMAND, "strikethrough");
    setShowColorPicker(null);
  }

  function handleBlockType(type: BlockType) {
    setShowColorPicker(null);
    if (type === "paragraph") {
      formatParagraph(editor);
      return;
    }
    if (type === "bullet") {
      formatBulletList(editor, blockType);
      return;
    }
    if (type === "number") {
      formatNumberedList(editor, blockType);
      return;
    }
    if (type === "check") {
      formatCheckList(editor, blockType);
      return;
    }

    if (type === "h1" || type === "h2" || type === "h3") {
      formatHeading(editor, blockType, type);
      return;
    }

    if (type === "quote") {
      formatQuote(editor, blockType);
      return;
    }

    if (type === "code") {
      formatCode(editor, blockType);
      return;
    }
  }

  const renderToolbarItem = (key: ToolbarItemKey) => {
    const item = TOOLBAR_ITEMS_MAP[key];

    switch (key) {
      case "undo":
        return (
          <Tooltip key={key} content={item.label} placement="bottom" delay={TOOLTIP_DELAY}>
            <EditorButton aria-label={item.label} isDisabled={!canUndo} isMobile={isMobile} onPress={handleUndo}>
              {item.icon}
            </EditorButton>
          </Tooltip>
        );

      case "redo":
        return (
          <Tooltip key={key} content={item.label} placement="bottom" delay={TOOLTIP_DELAY}>
            <EditorButton aria-label={item.label} isDisabled={!canRedo} isMobile={isMobile} onPress={handleRedo}>
              {item.icon}
            </EditorButton>
          </Tooltip>
        );

      case "bold":
        return (
          <Tooltip key={key} content={item.label} placement="bottom" delay={TOOLTIP_DELAY}>
            <EditorButton
              aria-label={item.label}
              isMobile={isMobile}
              variant={isBold ? "solid" : "light"}
              onPress={handleBold}
            >
              {item.icon}
            </EditorButton>
          </Tooltip>
        );

      case "italic":
        return (
          <Tooltip key={key} content={item.label} placement="bottom" delay={TOOLTIP_DELAY}>
            <EditorButton
              aria-label={item.label}
              isMobile={isMobile}
              variant={isItalic ? "solid" : "light"}
              onPress={handleItalic}
            >
              {item.icon}
            </EditorButton>
          </Tooltip>
        );

      case "underline":
        return (
          <Tooltip key={key} content={item.label} placement="bottom" delay={TOOLTIP_DELAY}>
            <EditorButton
              aria-label={item.label}
              isMobile={isMobile}
              variant={isUnderline ? "solid" : "light"}
              onPress={handleUnderline}
            >
              {item.icon}
            </EditorButton>
          </Tooltip>
        );

      case "strikethrough":
        return (
          <Tooltip key={key} content={item.label} placement="bottom" delay={TOOLTIP_DELAY}>
            <EditorButton
              aria-label={item.label}
              isMobile={isMobile}
              variant={isStrikethrough ? "solid" : "light"}
              onPress={handleStrikethrough}
            >
              {item.icon}
            </EditorButton>
          </Tooltip>
        );

      case "textColor":
        return (
          <Tooltip key={key} content={item.label} placement="bottom" delay={TOOLTIP_DELAY}>
            <div ref={textColorButtonRef}>
              <EditorButton
                aria-label={item.label}
                isMobile={isMobile}
                onPress={() => {
                  if (textColorButtonRef.current) {
                    const rect = textColorButtonRef.current.getBoundingClientRect();
                    setColorPickerPos({
                      top: rect.top - 46,
                      left: rect.left + rect.width / 2,
                    });
                  }
                  setShowColorPicker("text");
                }}
              >
                <div style={{ color: textColor }}>{item.icon}</div>
              </EditorButton>
            </div>
          </Tooltip>
        );

      case "backgroundColor":
        return (
          <Tooltip key={key} content={item.label} placement="bottom" delay={TOOLTIP_DELAY}>
            <div ref={bgColorButtonRef}>
              <EditorButton
                aria-label={item.label}
                isMobile={isMobile}
                onPress={() => {
                  if (bgColorButtonRef.current) {
                    const rect = bgColorButtonRef.current.getBoundingClientRect();
                    setColorPickerPos({
                      top: rect.top - 46,
                      left: rect.left + rect.width / 2,
                    });
                  }
                  setShowColorPicker("background");
                }}
                style={{
                  backgroundColor: textBgColor === "#fff" || textBgColor === "#000" ? "transparent" : textBgColor,
                }}
              >
                {item.icon}
              </EditorButton>
            </div>
          </Tooltip>
        );

      case "paragraph":
        return (
          <Tooltip key={key} content={item.label} placement="bottom" delay={TOOLTIP_DELAY}>
            <EditorButton
              aria-label={item.label}
              isMobile={isMobile}
              variant={blockType === "paragraph" ? "solid" : "light"}
              onPress={() => handleBlockType("paragraph")}
            >
              {item.icon}
            </EditorButton>
          </Tooltip>
        );

      case "heading1":
        return (
          <Tooltip key={key} content={item.label} placement="bottom" delay={TOOLTIP_DELAY}>
            <EditorButton
              aria-label={item.label}
              isMobile={isMobile}
              variant={blockType === "h1" ? "solid" : "light"}
              onPress={() => handleBlockType("h1")}
            >
              {item.icon}
            </EditorButton>
          </Tooltip>
        );

      case "heading2":
        return (
          <Tooltip key={key} content={item.label} placement="bottom" delay={TOOLTIP_DELAY}>
            <EditorButton
              aria-label={item.label}
              isMobile={isMobile}
              variant={blockType === "h2" ? "solid" : "light"}
              onPress={() => handleBlockType("h2")}
            >
              {item.icon}
            </EditorButton>
          </Tooltip>
        );

      case "heading3":
        return (
          <Tooltip key={key} content={item.label} placement="bottom" delay={TOOLTIP_DELAY}>
            <EditorButton
              aria-label={item.label}
              isMobile={isMobile}
              variant={blockType === "h3" ? "solid" : "light"}
              onPress={() => handleBlockType("h3")}
            >
              {item.icon}
            </EditorButton>
          </Tooltip>
        );

      case "bulletList":
        return (
          <Tooltip key={key} content={item.label} placement="bottom" delay={TOOLTIP_DELAY}>
            <EditorButton
              aria-label={item.label}
              variant={blockType === "bullet" ? "solid" : "light"}
              isMobile={isMobile}
              onPress={() => handleBlockType("bullet")}
            >
              {item.icon}
            </EditorButton>
          </Tooltip>
        );

      case "numberedList":
        return (
          <Tooltip key={key} content={item.label} placement="bottom" delay={TOOLTIP_DELAY}>
            <EditorButton
              aria-label={item.label}
              isMobile={isMobile}
              variant={blockType === "number" ? "solid" : "light"}
              onPress={() => handleBlockType("number")}
            >
              {item.icon}
            </EditorButton>
          </Tooltip>
        );

      case "checkList":
        return (
          <Tooltip key={key} content={item.label} placement="bottom" delay={TOOLTIP_DELAY}>
            <EditorButton
              aria-label={item.label}
              isMobile={isMobile}
              variant={blockType === "check" ? "solid" : "light"}
              onPress={() => handleBlockType("check")}
            >
              {item.icon}
            </EditorButton>
          </Tooltip>
        );

      case "quote":
        return (
          <Tooltip key={key} content={item.label} placement="bottom" delay={TOOLTIP_DELAY}>
            <EditorButton
              aria-label={item.label}
              isMobile={isMobile}
              variant={blockType === "quote" ? "solid" : "light"}
              onPress={() => handleBlockType("quote")}
            >
              {item.icon}
            </EditorButton>
          </Tooltip>
        );

      case "codeBlock":
        return (
          <Tooltip key={key} content={item.label} placement="bottom" delay={TOOLTIP_DELAY}>
            <EditorButton
              aria-label={item.label}
              isMobile={isMobile}
              variant={blockType === "code" ? "solid" : "light"}
              onPress={() => handleBlockType("code")}
            >
              {item.icon}
            </EditorButton>
          </Tooltip>
        );

      case "link":
        return (
          <Tooltip key={key} content={item.label} placement="bottom" delay={TOOLTIP_DELAY}>
            <EditorButton
              aria-label={item.label}
              isMobile={isMobile}
              variant={isLink ? "solid" : "light"}
              onPress={insertLink}
            >
              {item.icon}
            </EditorButton>
          </Tooltip>
        );

      case "code":
        return (
          <Tooltip key={key} content={item.label} placement="bottom" delay={TOOLTIP_DELAY}>
            <EditorButton
              aria-label={item.label}
              isMobile={isMobile}
              variant={isCode ? "solid" : "light"}
              onPress={() => editor.dispatchCommand(FORMAT_TEXT_COMMAND, "code")}
            >
              {item.icon}
            </EditorButton>
          </Tooltip>
        );

      case "table":
        return (
          <Tooltip key={key} content={item.label} placement="bottom" delay={TOOLTIP_DELAY}>
            <EditorButton
              aria-label={item.label}
              isMobile={isMobile}
              onPress={() =>
                editor.dispatchCommand(INSERT_TABLE_COMMAND, {
                  rows: "4",
                  columns: "3",
                  includeHeaders: { columns: false, rows: true },
                })
              }
            >
              {item.icon}
            </EditorButton>
          </Tooltip>
        );

      case "horizontalRule":
        return (
          <Tooltip key={key} content={item.label} placement="bottom" delay={TOOLTIP_DELAY}>
            <EditorButton
              aria-label={item.label}
              isMobile={isMobile}
              onPress={() => editor.dispatchCommand(INSERT_HORIZONTAL_RULE_COMMAND, undefined)}
            >
              {item.icon}
            </EditorButton>
          </Tooltip>
        );

      case "indent":
        return (
          <Tooltip key={key} content={item.label} placement="bottom" delay={TOOLTIP_DELAY}>
            <EditorButton
              aria-label={item.label}
              isMobile={isMobile}
              variant="light"
              onPress={() => editor.dispatchCommand(INDENT_CONTENT_COMMAND, undefined)}
            >
              {item.icon}
            </EditorButton>
          </Tooltip>
        );

      case "outdent":
        return (
          <Tooltip key={key} content={item.label} placement="bottom" delay={TOOLTIP_DELAY}>
            <EditorButton
              aria-label={item.label}
              isMobile={isMobile}
              variant="light"
              onPress={() => editor.dispatchCommand(OUTDENT_CONTENT_COMMAND, undefined)}
            >
              {item.icon}
            </EditorButton>
          </Tooltip>
        );

      case "equation":
        return (
          <Tooltip key={key} content={item.label} placement="bottom" delay={TOOLTIP_DELAY}>
            <EditorButton aria-label={item.label} isMobile={isMobile} variant="light" onPress={onEquationModalOpen}>
              {item.icon}
            </EditorButton>
          </Tooltip>
        );

      default:
        return null;
    }
  };

  useEffect(() => {
    return mergeRegister(
      editor.registerUpdateListener(({ editorState }) => {
        editorState.read(() => {
          $updateToolbar();
        });
      }),
      editor.registerCommand(
        SELECTION_CHANGE_COMMAND,
        () => {
          $updateToolbar();
          return false;
        },
        LowPriority
      ),
      editor.registerCommand(
        CAN_UNDO_COMMAND,
        (payload) => {
          setCanUndo(payload);
          return false;
        },
        LowPriority
      ),

      editor.registerCommand(
        CAN_REDO_COMMAND,
        (payload) => {
          setCanRedo(payload);
          return false;
        },
        LowPriority
      )
    );
  }, [editor, $updateToolbar]);

  return (
    <>
      <div className={clsx("scrollbar-hide flex items-center gap-1 overflow-x-scroll", className)}>
        {enabledToolbarItemKeys.map((key) => renderToolbarItem(key))}
      </div>
      {showColorPicker === "text" && colorPickerPos && (
        <ColorPicker
          colors={TEXT_COLORS}
          onChange={(color) => handleColorSelect("text", color)}
          onClickOutside={() => setShowColorPicker(null)}
          position={colorPickerPos}
        />
      )}

      {showColorPicker === "background" && colorPickerPos && (
        <ColorPicker
          colors={TEXT_BG_COLORS}
          onChange={(color) => handleColorSelect("background", color)}
          onClickOutside={() => setShowColorPicker(null)}
          position={colorPickerPos}
        />
      )}

      <DrawerAndModal
        id="equation-modal"
        title="Insert Equation"
        closeLabel="Close"
        isDismissable={true}
        externalState={{
          isOpen: isEquationModalOpen,
          onOpenChange: onEquationModalOpenChange,
        }}
      >
        <InsertEquationDialog activeEditor={editor} onClose={() => onEquationModalOpenChange()} />
      </DrawerAndModal>
    </>
  );
}

const blockTypes = ["paragraph", "bullet", "number", "check", "h1", "h2", "h3", "quote", "code"] as const;
type BlockType = (typeof blockTypes)[number];

const formatParagraph = (editor: LexicalEditor) => {
  editor.update(() => {
    const selection = $getSelection();
    $setBlocksType(selection, () => $createParagraphNode());
  });
};

const formatBulletList = (editor: LexicalEditor, blockType: BlockType) => {
  if (blockType !== "bullet") {
    editor.dispatchCommand(INSERT_UNORDERED_LIST_COMMAND, undefined);
  } else {
    formatParagraph(editor);
  }
};

const formatNumberedList = (editor: LexicalEditor, blockType: BlockType) => {
  if (blockType !== "number") {
    editor.dispatchCommand(INSERT_ORDERED_LIST_COMMAND, undefined);
  } else {
    formatParagraph(editor);
  }
};

const formatCheckList = (editor: LexicalEditor, blockType: BlockType) => {
  if (blockType !== "check") {
    editor.dispatchCommand(INSERT_CHECK_LIST_COMMAND, undefined);
  } else {
    formatParagraph(editor);
  }
};

const formatHeading = (editor: LexicalEditor, blockType: BlockType, headingSize: HeadingTagType) => {
  if (blockType !== headingSize) {
    editor.update(() => {
      const selection = $getSelection();
      $setBlocksType(selection, () => $createHeadingNode(headingSize));
    });
  } else {
    formatParagraph(editor);
  }
};

const formatQuote = (editor: LexicalEditor, blockType: string) => {
  if (blockType !== "quote") {
    editor.update(() => {
      const selection = $getSelection();
      $setBlocksType(selection, () => $createQuoteNode());
    });
  } else {
    formatParagraph(editor);
  }
};

const formatCode = (editor: LexicalEditor, blockType: string) => {
  if (blockType !== "code") {
    editor.update(() => {
      $addUpdateTag(SKIP_SELECTION_FOCUS_TAG);
      let selection = $getSelection();
      if (!selection) {
        return;
      }
      if (!$isRangeSelection(selection) || selection.isCollapsed()) {
        $setBlocksType(selection, () => $createCodeNode());
      } else {
        const textContent = selection.getTextContent();
        const codeNode = $createCodeNode();
        selection.insertNodes([codeNode]);
        selection = $getSelection();
        if ($isRangeSelection(selection)) {
          selection.insertRawText(textContent);
        }
      }
    });
  }
};
